/**
 * Nudge-question pool (pills rewrite P4) — the ONE client-side store every
 * on-page surface reads. Replaces the section-matching producers behind
 * `SectionBarView` (page-pills pool, section-voice grouping, section-scroll
 * matching): the server sends the page's VERIFIED nudge questions once at
 * page load, the pool deals random subsets, and scroll ROTATES to the next
 * subset locally — zero server calls, the "page is watching" feel intact.
 *
 * The view it produces keeps the `SectionBarView` SHAPE, so every living-bar
 * skin (glass capsule, sticky notes, rails …) renders unchanged.
 *
 * Asked-set: exact-normalized only, sessionStorage — a question the visitor
 * asked never resurfaces this session. The canonical-key NLP that lived in
 * page-pills.ts is gone: verified nudge questions are deduped at generation,
 * per Brian's rewrite.
 */
import type { SectionBarView } from "./section-voice.ts";

const ASKED_KEY = "bhai_asked_nudge_questions";

/** Questions shown in the "this section answers" slot per rotation. */
export const NUDGE_CURRENT = 2;
/** Questions shown in the "coming up" slot per rotation. */
export const NUDGE_NEXT = 2;

export function normalizeNudge(q: string): string {
  return q.replace(/\s+/g, " ").trim().toLowerCase();
}

let askedMemory: Set<string> | null = null;

function asked(): Set<string> {
  if (askedMemory) return askedMemory;
  askedMemory = new Set();
  try {
    const raw = sessionStorage.getItem(ASKED_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) for (const x of arr) if (typeof x === "string") askedMemory.add(x);
    }
  } catch {
    /* storage unavailable — memory-only */
  }
  return askedMemory;
}

export function markNudgeAsked(q: string): void {
  const k = normalizeNudge(q);
  if (!k) return;
  const set = asked();
  if (set.has(k)) return;
  set.add(k);
  try {
    sessionStorage.setItem(ASKED_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function wasNudgeAsked(q: string): boolean {
  return asked().has(normalizeNudge(q));
}

/** Tests + ?bh_new_session=1. */
export function clearAskedNudges(): void {
  askedMemory = new Set();
  try {
    sessionStorage.removeItem(ASKED_KEY);
  } catch {
    /* ignore */
  }
}

// ── The pool ───────────────────────────────────────────────────────────────

type PoolState = {
  /** Shuffled once per setNudgePool (one pageview) — rotation walks it. */
  order: string[];
  rotation: number;
  /** The pageview's deal seed, reused to derive each repetition's shuffle so
   *  the sequence is stable across re-renders within the pageview. */
  seed: number;
};

let state: PoolState = { order: [], rotation: 0, seed: 1 };

/** Customer display count for the FLOATING chips (0–4), delivered alongside
 *  the pool on the pageview event response. Module state next to the pool it
 *  governs: both are set before the section-bar view is pushed, so every
 *  render reads a consistent pair. Default 2 (the fleet default — also the
 *  fallback when an older server omits the field). */
const NUDGE_FLOAT_DEFAULT = 2;
const NUDGE_FLOAT_MAX = 4;
let floatCount = NUDGE_FLOAT_DEFAULT;

export function setNudgeFloatCount(n: number | undefined): void {
  floatCount =
    n == null || !Number.isFinite(n)
      ? NUDGE_FLOAT_DEFAULT
      : Math.max(0, Math.min(NUDGE_FLOAT_MAX, Math.floor(n)));
}

export function nudgeFloatCount(): number {
  return floatCount;
}

/** Tenant's PHONE floating count (2026-10-14). null = not set: each phone
 *  surface applies its built-in cap to nudgeFloatCount(), exactly as before. */
let mobileCount: number | null = null;

export function setNudgeMobileCount(n: number | undefined): void {
  mobileCount =
    n == null || !Number.isFinite(n) ? null : Math.max(0, Math.min(NUDGE_FLOAT_MAX, Math.floor(n)));
}

export function nudgeMobileCount(): number | null {
  return mobileCount;
}

/** The chip budget a phone surface paints: the tenant's phone count when set,
 *  otherwise the floating count under that surface's own built-in cap. */
export function phoneChipBudget(builtInCap: number): number {
  return mobileCount ?? Math.min(builtInCap, floatCount);
}

/** Deterministic Fisher–Yates (mulberry32) — one shuffle per pageview so
 *  re-renders within the pageview are stable. */
function shuffle(items: readonly string[], seed: number): string[] {
  const out = [...items];
  let s = seed >>> 0 || 1;
  const rand = () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Install the page's verified nudge questions (events channel / SPA nav).
 *  `seed` defaults to a per-call random so each pageview gets its own deal.
 *  `borrowed` (same-page-type questions lent by other pages when this page
 *  is under the display floor) shuffles as its OWN tier appended after the
 *  native deal — rotation reaches natives first, borrowed only as fill, so
 *  the page leads with its own questions but never feels empty. */
export function setNudgePool(
  questions: readonly string[],
  seed?: number,
  borrowed: readonly string[] = [],
): void {
  const seen = new Set<string>();
  const cleanTier = (items: readonly string[]): string[] => {
    const out: string[] = [];
    for (const raw of items) {
      const t = (raw ?? "").replace(/\s+/g, " ").trim();
      if (t.length < 8) continue;
      const k = normalizeNudge(t);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
    }
    return out;
  };
  const s = seed ?? (Math.random() * 2 ** 32) | 0;
  const native = cleanTier(questions);
  const fill = cleanTier(borrowed);
  state = {
    order: [...shuffle(native, s), ...shuffle(fill, s ^ 0x9e3779b9)],
    rotation: 0,
    seed: s,
  };
}

/** How many independent shuffles the deal sequence concatenates.
 *
 *  Windowing a pool of N straight through gives ceil(N / per) windows, so a
 *  pool that happens to equal one window never changes no matter how far the
 *  visitor scrolls — and most pages sit near the display floor, so that is
 *  the COMMON case, not an edge one. Concatenating a few independent shuffles
 *  makes the sequence longer than the pool, so the same questions keep
 *  appearing in DIFFERENT pairings instead of freezing. (Brian, 2026-09-12.)
 *
 *  Three: enough that a 4-question pool yields three distinct windows, few
 *  enough that a visitor scrolling a long page does not see the deal loop
 *  visibly. */
export const DEAL_REPEATS = 3;

/** The deal sequence: DEAL_REPEATS independent shuffles of the unasked pool,
 *  concatenated. Each repetition reshuffles off a derived seed, so repetition
 *  k is not repetition 0 again. Derived (not stored) so it always reflects
 *  what is still unasked. */
function dealSequence(): string[] {
  const pool = unasked();
  if (pool.length === 0) return [];
  const out: string[] = [];
  for (let k = 0; k < DEAL_REPEATS; k++) {
    // k = 0 keeps the install order so the FIRST paint matches the deal the
    // pageview chose; later repetitions vary.
    out.push(...(k === 0 ? pool : shuffle(pool, (state.seed ^ (k * 0x9e3779b9)) >>> 0)));
  }
  return out;
}

export function clearNudgePool(): void {
  state = { order: [], rotation: 0, seed: 1 };
}

// Scroll-ahead sections (GH #590) are GONE.
//
// They sourced their questions from `embeddings.question` — the raw ingest
// question-gen output, gated on `enabled` alone. That bypassed the whole
// nudge pipeline: no probe, no responsiveness judge, no display floor, no
// `status = 'verified'`, and not counted by the nudge settings. A question
// nobody approved could therefore be shown to a visitor as a pill (observed
// live: "Do you offer penile implant surgery?" on t52w4reb, present ONLY in
// embeddings.question and never in the verified pool).
//
// Positioning questions by scroll offset also only earns its complexity on a
// page long enough for "below the fold" to mean something (Brian, 2026-09-12).
// The useful half — pills CHANGING as the visitor moves down the page — is
// kept, and was never section-dependent: the scroll driver already rotates
// the window on each document third (`onThirdChange` → rotateNudges), which
// is the same felt behaviour sourced entirely from verified nudges.

/** Advance to the next window (scroll dwell thresholds — the caller decides
 *  when; today each document third). Wraps; a fully-asked pool stays
 *  inactive. */
export function rotateNudges(): void {
  if (state.order.length === 0) return;
  // Modulo the DEAL SEQUENCE, not the pool. Taking it from the pool capped a
  // one-window pool at a single window, so rotation silently no-op'd for
  // exactly the pools that most need to vary.
  const windows = Math.max(1, Math.ceil(dealSequence().length / (NUDGE_CURRENT + NUDGE_NEXT)));
  state.rotation = (state.rotation + 1) % windows;
}

function unasked(): string[] {
  return state.order.filter((q) => !wasNudgeAsked(q));
}

/** The full unasked pool (panel "Suggested", voice pill selection, chat-open
 *  candidates). Order = the pageview's shuffled deal, stable across reads. */
export function nudgePool(): string[] {
  return unasked();
}

/** The current rotation window in the `SectionBarView` SHAPE, so every skin
 *  renders unchanged. Headings are generic — the section framing is retired. */
export function currentNudgeView(): SectionBarView {
  const per = NUDGE_CURRENT + NUDGE_NEXT;
  // Deal from the REPEATED sequence, not straight through the pool: a pool
  // the size of one window would otherwise never change as the visitor
  // scrolls, which is the common case near the display floor.
  const seq = dealSequence();
  const windows = Math.max(1, Math.ceil(seq.length / per));
  const at = (state.rotation % windows) * per;
  // A window must never show the same question twice — the seam between two
  // repetitions can land the same question either side of it. Walk forward
  // from `at`, skipping anything already in this window.
  const window: string[] = [];
  const taken = new Set<string>();
  for (let i = 0; i < seq.length && window.length < per; i++) {
    const q = seq[(at + i) % seq.length]!;
    const k = normalizeNudge(q);
    if (taken.has(k)) continue;
    taken.add(k);
    window.push(q);
  }
  const current = window.slice(0, NUDGE_CURRENT);
  const next = window.slice(NUDGE_CURRENT, per);
  if (current.length === 0) {
    return {
      active: false,
      sectionIndex: 0,
      currentHeading: "",
      currentQuestions: [],
      nextHeading: null,
      nextQuestions: [],
      comingUpPool: [],
      lead: "",
      ctaLabel: "",
      openWithQuestions: [],
    };
  }
  return {
    active: true,
    sectionIndex: state.rotation,
    currentHeading: "On this page",
    currentQuestions: current,
    nextHeading: next.length > 0 ? "Also worth asking" : null,
    nextQuestions: next,
    comingUpPool: seq.slice(at + NUDGE_CURRENT, at + per + NUDGE_NEXT),
    lead: "",
    ctaLabel: "Get the answers now",
    openWithQuestions: [...current, ...next],
  };
}
